import { useDocumentsRepository } from '@Hooks/useRepository';
import { AiProvider, type CreateExportDefinitionFromAiRequest } from '@Model';
import { DynamicExportDefinitionRepository } from '@Repositories/Documents';
import { EnumMessageLevel, isNullOrEmpty, useToasts } from '@synqo/frontend-component-library';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

export function useGenerateExportDefinition() {
    const exportDefinitions = useDocumentsRepository(DynamicExportDefinitionRepository);
    const { pushToast, pushAllToasts } = useToasts();

    const { mutateAsync, isPending } = useMutation({
        mutationFn: (request: CreateExportDefinitionFromAiRequest) =>
            exportDefinitions.generateDynamicExportFromAi(request),
    });

    const handleGenerate = useCallback(
        async (provider: AiProvider, prompt: string, category: string, key: string) => {
            if (!prompt.trim()) {
                return;
            }

            const request: CreateExportDefinitionFromAiRequest = {
                prompt,
                provider,
                category,
                key,
            };

            try {
                const response = await mutateAsync(request);

                pushAllToasts(response.messages);

                const generatedYaml = response.data?.definition;

                if (isNullOrEmpty(generatedYaml)) {
                    console.error('No definition in result:', response);
                    pushToast({
                        level: EnumMessageLevel.Danger,
                        title: 'Fehler beim Generieren',
                        message: 'Es konnte kein Skript generiert werden.',
                    });

                    return '';
                }

                return generatedYaml;
            } catch (error) {
                console.error('Failed to generate export definition:', error);
                pushToast({
                    level: EnumMessageLevel.Danger,
                    title: 'Fehler beim Generieren',
                    message: 'Es gab einen Fehler beim generieren des Exportskripts. Bitte versuchen sie es nochmal.',
                });

                return '';
            }
        },
        [mutateAsync, pushToast, pushAllToasts],
    );

    return {
        generate: handleGenerate,
        isGenerating: isPending,
    };
}
